import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { putProblemTestcases } from "service/api/content/postApi";
import { DetailActionBtn } from "components/button";

export interface TestcaseRow {
  id?: number;
  input: string;
  output: string;
  show: number;
}

interface Props {
  problemId: string;
  initialRows: TestcaseRow[];
}

const Cover = styled.section`
  margin-top: 48px;

  .heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  h2 {
    margin: 0;
    color: #232d42;
    font-size: 18px;
    font-weight: 500;
  }

  .guide {
    margin: 0 0 16px;
    color: #6c757d;
    font-size: 14px;
    line-height: 1.5;
  }
`;

const Sheet = styled.div`
  overflow: auto;
  border: 1px solid #c5ced6;
`;

const Table = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  background: #fff;
  table-layout: fixed;

  th,
  td {
    border: 1px solid #d5dee6;
    vertical-align: top;
  }

  th {
    height: 36px;
    background: #e8edf2;
    color: #232d42;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
  }

  .row-no {
    width: 52px;
    background: #f7f9fb;
    color: #6c757d;
    font-size: 13px;
    text-align: center;
    vertical-align: middle;
  }

  .show-cell {
    width: 96px;
    text-align: center;
    vertical-align: middle;
  }

  .action-cell {
    width: 168px;
    text-align: center;
    vertical-align: middle;
  }

  textarea {
    display: block;
    width: 100%;
    min-height: 88px;
    padding: 10px 12px;
    border: none;
    outline: none;
    resize: vertical;
    background: transparent;
    color: #232d42;
    font-family: Consolas, "Courier New", monospace;
    font-size: 14px;
    line-height: 1.5;
  }

  textarea:focus {
    box-shadow: inset 0 0 0 2px #4c8bf5;
  }

  .mini {
    margin: 0 4px;
    padding: 4px 8px;
    border: 1px solid #c5ced6;
    background: #fff;
    color: #4a4a4a;
    font-size: 12px;
    cursor: pointer;
  }

  .mini:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;

  .status {
    color: #2f7d32;
    font-size: 14px;
  }

  .status.error {
    color: #c62828;
  }

  .actions {
    display: flex;
    gap: 12px;
  }
`;

function cloneRows(rows: TestcaseRow[]) {
  return rows.map((row) => ({
    id: row.id,
    input: row.input ?? "",
    output: row.output ?? "",
    show: row.show === 0 ? 0 : 1,
  }));
}

export function TestcaseSheet({ problemId, initialRows }: Props) {
  const [rows, setRows] = useState<TestcaseRow[]>(() => cloneRows(initialRows));
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [status, setStatus] = useState<{ error: boolean; text: string } | null>(null);

  useEffect(() => {
    setRows(cloneRows(initialRows));
    setStatus(null);
  }, [problemId, initialRows]);

  const updateRow = (index: number, patch: Partial<TestcaseRow>) => {
    setRows((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
    setStatus(null);
  };

  const moveRow = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= rows.length) return;
    const next = [...rows];
    const [row] = next.splice(index, 1);
    next.splice(nextIndex, 0, row);
    setRows(next);
    setStatus(null);
  };

  const addRow = () => {
    setRows((prev) => [...prev, { input: "", output: "", show: 1 }]);
    setStatus(null);
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
    setStatus(null);
  };

  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setStatus(null);
    try {
      const res: any = await putProblemTestcases(problemId, {
        testcases: rows.map((row, index) => ({
          ...(row.id != null ? { id: row.id } : {}),
          input: row.input,
          output: row.output,
          show: row.show === 0 ? 0 : 1,
          order: index + 1,
        })),
      });

      if (res?.status === 200 && Array.isArray(res.data?.data)) {
        setRows(
          res.data.data.map((row: TestcaseRow) => ({
            id: row.id,
            input: row.input ?? "",
            output: row.output ?? "",
            show: row.show === 0 ? 0 : 1,
          })),
        );
        setStatus({ error: false, text: "테스트케이스를 저장했습니다." });
        return;
      }

      const message = res?.response?.data?.message || "테스트케이스 저장에 실패했습니다.";
      setStatus({ error: true, text: Array.isArray(message) ? message.join(", ") : String(message) });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Cover>
      <div className="heading">
        <h2>테스트케이스 관리</h2>
      </div>
      <p className="guide">
        한 행이 테스트케이스 하나입니다. 입력과 기대 출력을 나란히 수정하고, 공개를 끄면 학습 화면 예시에는 나오지 않습니다. 채점은 공개 여부와 관계없이 이 표의 행을 모두 사용합니다.
      </p>
      <Sheet>
        <Table>
          <colgroup>
            <col style={{ width: 52 }} />
            <col />
            <col />
            <col style={{ width: 96 }} />
            <col style={{ width: 168 }} />
          </colgroup>
          <thead>
            <tr>
              <th />
              <th>입력</th>
              <th>기대 출력</th>
              <th>공개</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td className="row-no" />
                <td colSpan={4} style={{ padding: 16, color: "#6c757d" }}>
                  등록된 테스트케이스가 없습니다. 행 추가를 눌러 입력과 기대 출력을 넣으세요.
                </td>
              </tr>
            )}
            {rows.map((row, index) => (
              <tr key={row.id ?? `new-${index}`}>
                <td className="row-no">{index + 1}</td>
                <td>
                  <textarea
                    value={row.input}
                    aria-label={`${index + 1}번 입력`}
                    onChange={(e) => updateRow(index, { input: e.target.value })}
                  />
                </td>
                <td>
                  <textarea
                    value={row.output}
                    aria-label={`${index + 1}번 기대 출력`}
                    onChange={(e) => updateRow(index, { output: e.target.value })}
                  />
                </td>
                <td className="show-cell">
                  <input
                    type="checkbox"
                    checked={row.show !== 0}
                    aria-label={`${index + 1}번 학습 화면 공개`}
                    onChange={(e) => updateRow(index, { show: e.target.checked ? 1 : 0 })}
                  />
                </td>
                <td className="action-cell">
                  <button type="button" className="mini" disabled={index === 0} onClick={() => moveRow(index, -1)}>
                    위
                  </button>
                  <button
                    type="button"
                    className="mini"
                    disabled={index === rows.length - 1}
                    onClick={() => moveRow(index, 1)}
                  >
                    아래
                  </button>
                  <button type="button" className="mini" onClick={() => removeRow(index)}>
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>
      <Footer>
        <p className={status?.error ? "status error" : "status"}>{status?.text}</p>
        <div className="actions">
          <DetailActionBtn isFill={false} label="행 추가" onClick={addRow} />
          <DetailActionBtn label={saving ? "저장 중" : "테스트케이스 저장"} onClick={save} />
        </div>
      </Footer>
    </Cover>
  );
}
